/**
 * Whether a thrown value from a `callApi` request is a definitive rejection of the request — an
 * HTTP 4xx response. The generated client throws the Response for any non-2xx, carrying a numeric
 * `status`; a transport failure (offline / DNS / CORS) rejects with no `status` at all.
 * Discriminating on `status` (rather than the fetch error message, which is 'Failed to fetch' in
 * the browser vs 'Network request failed' on native) is platform-agnostic.
 *
 * Used by the optimistic-offline coupon flows: only a 4xx means the server looked at the code and
 * rejected it (deleted/revoked → e.g. 404/410), so only a 4xx may drop ownership or delete a
 * persisted code. A 5xx (backend outage, failing proxy, captive portal answering with an error
 * page) and a transport failure are both transient — destroying a paid unlock over them would be
 * unrecoverable. 401 (stale auth token), 408 (request timeout) and 429 (rate limiting — boot
 * revalidation fires a burst of one call per persisted code) are 4xx but say nothing about the
 * code itself, so they're treated as transient too.
 */
export const isHttpClientError = (error: unknown): boolean => {
	const status = (error as { status?: number })?.status
	return (
		typeof status === 'number' && status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429
	)
}
